import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  XCircleIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import CandidatesTable from './screenings/CandidatesTable';
import CandidateInsightModal from '../components/results/CandidateInsightModal';
import PublicAssessmentScoreModal from '../components/modals/PublicAssessmentScoreModal';
import PublicInterviewReplayModal from '../components/modals/PublicInterviewReplayModal';
import ReviewerNameModal from './candidateReview/ReviewerNameModal';
import ReviewActivityDrawer from './candidateReview/ReviewActivityDrawer';
import { buildScreeningAssessmentModuleMeta } from './screenings/assessmentModuleColumns';
import { buildCandidateComparator } from './screenings/candidateSort';

const REVIEWER_NAME_STORAGE_PREFIX = 'shareReviewerName:';

const readStoredReviewerName = (shareCode) => {
  if (!shareCode || typeof window === 'undefined') return '';
  try {
    return (window.localStorage.getItem(REVIEWER_NAME_STORAGE_PREFIX + shareCode) || '').trim();
  } catch {
    return '';
  }
};

const writeStoredReviewerName = (shareCode, name) => {
  if (!shareCode || typeof window === 'undefined') return;
  try {
    if (name) {
      window.localStorage.setItem(REVIEWER_NAME_STORAGE_PREFIX + shareCode, name);
    } else {
      window.localStorage.removeItem(REVIEWER_NAME_STORAGE_PREFIX + shareCode);
    }
  } catch {
    /* ignore */
  }
};

const describeQueuedAction = (updates, candidateName) => {
  const target = candidateName?.trim() || 'this candidate';
  if ('shortlistDecision' in updates) {
    const d = updates.shortlistDecision;
    if (d === 'selected') return `Shortlist ${target}`;
    if (d === 'rejected') return `Reject ${target}`;
    if (d === 'pending') return `Mark ${target} as on-hold`;
    if (d === 'offered') return `Mark ${target} as offered`;
    if (!d) return `Clear decision for ${target}`;
  }
  if ('shortlistNote' in updates) {
    return updates.shortlistNote ? `Add a note on ${target}` : `Remove note on ${target}`;
  }
  return 'Save change';
};

const formatLocationDisplay = (location, locationType) => {
  const locations = Array.isArray(location)
    ? location.filter(Boolean).map((item) => String(item).trim()).filter(Boolean)
    : location
      ? [String(location).trim()]
      : [];
  const locationValue = locations.length ? locations.join(', ') : '';
  const locationTypeValue = locationType ? String(locationType).trim() : '';
  if (locationValue && locationTypeValue) return `${locationValue} (${locationTypeValue})`;
  return locationValue || locationTypeValue || '';
};

const CandidateReviewPublic = () => {
  const { shareCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  // Detail hydration for the review modal, mirroring the recruiter page's
  // candidateInsightDetailsById / candidateInsightDetailsLoading pair. The table
  // row alone lacks the profile detail the modal renders, so it is fetched on
  // open and cached per candidate for the rest of the session.
  const [candidateDetailsById, setCandidateDetailsById] = useState({});
  const [candidateDetailsLoading, setCandidateDetailsLoading] = useState(false);
  const [assessmentModal, setAssessmentModal] = useState({ open: false, candidate: null });
  const [interviewModal, setInterviewModal] = useState({
    open: false,
    candidate: null,
    initialTab: 'analysis',
  });
  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);

  const [scoreFilters, setScoreFilters] = useState({
    overall: { mode: null, value: null, min: null, max: null },
    resume: { mode: null, value: null, min: null, max: null },
    assessmentMin: null,
    interview: { mode: null, value: null, min: null, max: null },
    status: null,
  });
  const [metaFilters, setMetaFilters] = useState({
    proctoring: null,
    experience: null,
    age: { mode: null, value: null, min: null, max: null },
    currentCtc: null,
    noticePeriod: null,
    completedAtFrom: null,
    completedAtTo: null,
    education: [],
    skills: [],
    location: [],
    company: [],
    currentRole: [],
    gender: [],
  });

  const [shortlistDecisions, setShortlistDecisions] = useState({});
  const [shortlistNotes, setShortlistNotes] = useState({});
  const [reviewerName, setReviewerName] = useState(() => readStoredReviewerName(shareCode));
  const [nameModal, setNameModal] = useState({
    open: false,
    mode: 'capture',
    actionLabel: '',
    queued: null,
    required: false,
  });
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const viewLoggedRef = useRef(false);

  useEffect(() => {
    setReviewerName(readStoredReviewerName(shareCode));
    viewLoggedRef.current = false;
  }, [shareCode]);

  useEffect(() => {
    fetchData();
  }, [shareCode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`/api/screenings/public/candidate-review/${shareCode}`);
      setData(res.data);

      const decisions = {};
      const notes = {};
      res.data?.candidates?.forEach(c => {
        const decision = c.shortlistDecision || c.decisionStatus || null;
        const note = c.shortlistNote || c.decisionNotes || null;
        if (decision) decisions[c._id] = decision;
        if (note) notes[c._id] = note;
      });
      setShortlistDecisions(decisions);
      setShortlistNotes(notes);
      setActivityLog(Array.isArray(res.data?.activityLog) ? res.data.activityLog : []);
    } catch (err) {
      if (err.response?.data?.code === 'LINK_INACTIVE') {
        setError({ title: 'Link Deactivated', message: 'This review link has been deactivated by the recruiter.' });
      } else if (err.response?.status === 404) {
        setError({ title: 'Not Found', message: 'This review link does not exist.' });
      } else {
        setError({ title: 'Error', message: err.response?.data?.message || 'Failed to load candidates.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshActivityLog = useCallback(async () => {
    try {
      const res = await axios.get(`/api/screenings/public/candidate-review/${shareCode}/activity`);
      setActivityLog(Array.isArray(res.data?.activityLog) ? res.data.activityLog : []);
    } catch {
      /* ignore */
    }
  }, [shareCode]);

  const logReviewerView = useCallback(
    async (nameOverride) => {
      const effectiveName = String(nameOverride || reviewerName || '').trim();
      if (!effectiveName || viewLoggedRef.current) return false;
      viewLoggedRef.current = true;
      try {
        await axios.post(`/api/screenings/public/candidate-review/${shareCode}/view`, {
          reviewerName: effectiveName,
        });
        refreshActivityLog();
        return true;
      } catch (err) {
        viewLoggedRef.current = false;
        return false;
      }
    },
    [reviewerName, shareCode, refreshActivityLog]
  );

  useEffect(() => {
    if (loading || error || !data) return;
    if (reviewerName) {
      logReviewerView();
      return;
    }
    setNameModal((prev) =>
      prev.open
        ? prev
        : {
          open: true,
          mode: 'capture',
          actionLabel: 'Open this review link',
          queued: null,
          required: true,
        }
    );
  }, [loading, error, data, reviewerName, logReviewerView]);

  const performShortlistPatch = useCallback(
    async (candidateId, updates, nameOverride) => {
      const effectiveName = (nameOverride || reviewerName || '').trim();
      if (!effectiveName) return false;
      try {
        const payload = { candidateId, reviewerName: effectiveName };
        let updatedStatus = shortlistDecisions[candidateId] || '';
        let updatedNotes = shortlistNotes[candidateId] || '';

        if ('shortlistDecision' in updates) {
          payload.decisionStatus = updates.shortlistDecision || '';
          updatedStatus = updates.shortlistDecision || '';
        }
        if ('shortlistNote' in updates) {
          payload.decisionNotes = updates.shortlistNote || '';
          updatedNotes = updates.shortlistNote || '';
        }

        await axios.patch(
          `/api/screenings/public/candidate-review/${shareCode}/decision`,
          payload
        );

        // Sync the lookup maps the table reads from. The table optimistically
        // updates these only when patchCandidateShortlistField returns truthy,
        // but on the first action we queue the patch behind the name modal and
        // return false, so we must update them here once the patch succeeds.
        if ('shortlistDecision' in updates) {
          setShortlistDecisions((prev) => ({
            ...prev,
            [candidateId]: updates.shortlistDecision || null,
          }));
        }
        if ('shortlistNote' in updates) {
          setShortlistNotes((prev) => ({
            ...prev,
            [candidateId]: updates.shortlistNote || null,
          }));
        }

        setData((prev) => {
          if (!prev) return prev;
          const updatedCandidates = prev.candidates.map((c) =>
            c._id === candidateId
              ? {
                ...c,
                decisionStatus: updatedStatus,
                decisionNotes: updatedNotes,
                shortlistDecision: updatedStatus || null,
                shortlistNote: updatedNotes || null,
                decisionAt:
                  updatedStatus || updatedNotes ? new Date().toISOString() : null,
                decisionBy:
                  updatedStatus || updatedNotes
                    ? `${effectiveName} (via share link)`
                    : '',
              }
              : c
          );
          return { ...prev, candidates: updatedCandidates };
        });
        toast.success('Decision saved');
        // Refresh log so the latest action is reflected in the drawer.
        refreshActivityLog();
        return true;
      } catch (err) {
        if (err?.response?.data?.code === 'REVIEWER_NAME_REQUIRED') {
          setNameModal({
            open: true,
            mode: 'capture',
            actionLabel: '',
            queued: { candidateId, updates },
            required: true,
          });
          return false;
        }
        toast.error('Failed to save decision');
        return false;
      }
    },
    [reviewerName, shortlistDecisions, shortlistNotes, shareCode, refreshActivityLog]
  );

  const patchCandidateShortlistField = useCallback(
    async (candidateId, updates) => {
      if (!reviewerName) {
        const candidate = (data?.candidates || []).find((c) => c._id === candidateId);
        const candidateDisplayName =
          candidate?.candidateName || candidate?.name || candidate?.fullName || '';
        setNameModal({
          open: true,
          mode: 'capture',
          actionLabel: describeQueuedAction(updates, candidateDisplayName),
          queued: { candidateId, updates },
          required: true,
        });
        return false;
      }
      return performShortlistPatch(candidateId, updates);
    },
    [reviewerName, data, performShortlistPatch]
  );

  const handleNameSubmit = useCallback(
    async (name) => {
      const trimmed = String(name || '').trim();
      if (!trimmed) return;
      setReviewerName(trimmed);
      writeStoredReviewerName(shareCode, trimmed);
      const queued = nameModal.queued;
      setNameModal({ open: false, mode: 'capture', actionLabel: '', queued: null, required: false });
      await logReviewerView(trimmed);
      if (queued) {
        await performShortlistPatch(queued.candidateId, queued.updates, trimmed);
      }
    },
    [nameModal, performShortlistPatch, shareCode, logReviewerView]
  );

  const handleNameCancel = useCallback(() => {
    if (nameModal.required) return;
    setNameModal({ open: false, mode: 'capture', actionLabel: '', queued: null, required: false });
  }, [nameModal.required]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-[#007BFF] border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-gray-600 text-sm">Loading candidate review…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{error.title}</h2>
          <p className="text-gray-500 text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  // Org/screening view configuration forwarded by the share-link endpoint. It is
  // what keeps this page's table identical to the recruiter's shortlist tab —
  // without it every auth-derived default silently resolves to "off" here,
  // because a public link has no logged-in user to read them from.
  const viewConfig = data?.viewConfig || null;
  const multiModuleTableScoresEnabled = viewConfig?.features?.multi_module_table_scores === true;

  const screeningMeta = {
    // The SCREENING id — this previously held the interview id.
    id: data?.screening?.screeningId || null,
    name: data?.screening?.name,
    hasResumeScoring: data?.screening?.hasResumeScoring !== false,
    includeAssessment: data?.screening?.includeAssessment !== false,
    includeInterview: data?.screening?.includeInterview !== false,
    hasAssessment: data?.screening?.hasAssessment ?? !!data?.screening?.assessmentId,
    hasInterview: data?.screening?.hasInterview ?? !!data?.screening?.interviewId,
    assessmentId: data?.screening?.assessmentId,
    interviewId: data?.screening?.interviewId,
    // Mirrors ScreeningDetailPage's screeningMeta. Each of these was missing, and
    // each one silently removed something the recruiter can see:
    //  - isMovedCandidatesPool: without it a moved pool loses ALL score columns
    //  - assessment module meta: per-module score columns never rendered
    isMovedCandidatesPool: data?.screening?.isMovedCandidatesPool === true,
    // NOTE: `includeDocumentCollection` is deliberately NOT set here, even though
    // the endpoint sends it. In CandidatesTable it is the left side of an OR:
    //   resolvedShowDocumentsColumn = screeningMeta?.includeDocumentCollection === true || …
    // so setting it force-enables the documents column and no `showDocumentsColumn`
    // option can switch it back off. The cell then reads `documentStatusMap`, which
    // is recruiter-only state and absent from the public payload — so every row
    // renders "—" and the View button is inert. A dashes-only column reads as
    // broken, so the column stays hidden until the statuses are actually forwarded.
    multiModuleTableScoresEnabled,
    // The helper reads `screening.assessmentId` as an object; the endpoint sends
    // that shape as `assessmentMeta` so `assessmentId` can stay a plain id.
    ...buildScreeningAssessmentModuleMeta(
      { assessmentId: data?.screening?.assessmentMeta || null },
      multiModuleTableScoresEnabled
    ),
  };
  const isReviewUnlocked = !!reviewerName;

  const weightsConfig = viewConfig?.scoreWeights || null;

  // Show the "Selected Date" column only when EVERY shared candidate was actually
  // selected (has a shortlistedAt date). A link can be minted from the Candidates
  // tab for candidates who were never selected — for those the column is meaningless
  // (it would render "—" for all rows), so it is hidden entirely. When even one
  // shared candidate is not selected, the column is dropped for the whole list.
  const allCandidatesSelected =
    (data?.candidates?.length || 0) > 0 &&
    (data?.candidates || []).every((c) => Boolean(c.shortlistedAt));

  const tableOptions = {
    showShortlistedDate: allCandidatesSelected,
    // Mirrors the recruiter's Selected tab, which passes `false` explicitly.
    // candidatesTableLayout defaults this to TRUE (`options?.showRejectedDate ?? true`),
    // so omitting it showed the hiring manager a Rejected Date column on a list of
    // SELECTED candidates — a column the recruiter never sees on this tab.
    showRejectedDate: false,
    showStatus: true,
    hideColumnFilters: true,
    showInlineEmptyState: true,
    isShortlistView: true,
    // Public hiring-manager view: hide the row actions column. Its menu holds
    // recruiter-only actions (Shortlist/Reject/Resend/Delete) and depends on
    // actionMenuPosition state the public page does not provide — leaving it on
    // both crashed (setActionMenuPosition is not a function) and exposed those
    // actions to anyone with the share link.
    hideRowActions: true,
    // Tells CandidatesTable to drop anything that only works for a logged-in
    // recruiter (in-app /dashboard deep links).
    isPublicView: true,
    // Column parity with the recruiter's Selected tab. Most meta columns in
    // candidatesTableLayout default to hidden and are switched on ONLY by a saved
    // layout, so without these three the hiring manager saw roughly six columns
    // where the recruiter had configured a dozen — with the data already present
    // in the payload and simply not rendered.
    // Merged org-default-then-saved, matching how the recruiter page resolves it
    // (loadColPrefs: `{...orgDef.visibility, ...saved.visibility}`). A saved
    // layout is a partial override, not a replacement — shadowing the org
    // default with it would hide columns the recruiter still sees.
    columnVisibility: {
      ...(viewConfig?.ui?.defaultShortlistColumnVisibility || {}),
      ...(viewConfig?.columnLayout?.visibility || {}),
    },
    columnOrder: viewConfig?.columnLayout?.order || [],
    orgDefaultColumnOrder: viewConfig?.ui?.defaultShortlistColumnOrder || [],
    // Drives the store-location columns for store-based orgs.
    storeListEnabled: viewConfig?.features?.store_list === true,
    // Recruiter-only column, held off explicitly rather than by omission (it
    // already defaults to false) so the reason is recorded: the cell reads
    // `props.salaryRequestsMap`, recruiter-only state that is not in the public
    // payload. Forwarding the org flag alone would render an empty column, so
    // the flag and the data have to arrive together — same rule as `documents`.
    hasArmSalaryDetails: false,
  };

  // Same ordering the recruiter's Selected tab applies. Previously absent
  // entirely, so the reviewer got raw server order — a different ranking from the
  // one the recruiter was looking at when they shared the link.
  const candidateComparator = buildCandidateComparator({
    screeningMeta,
    weightsConfig,
    sortColumn: viewConfig?.columnLayout?.sortColumn || viewConfig?.ui?.defaultShortlistSortColumn || 'overallScore',
    sortDirection:
      viewConfig?.columnLayout?.sortDirection || viewConfig?.ui?.defaultShortlistSortDirection || 'desc',
  });
  const candidates = [...(data?.candidates || [])].sort(candidateComparator);
  const recruiterContact = data?.screening?.recruiterContact || null;
  const recruiterName = recruiterContact?.name ? String(recruiterContact.name).trim() : '';
  const evaluationBasis = [
    data?.screening?.hasResumeScoring !== false ? 'Resume' : null,
    data?.screening?.includeAssessment !== false ? 'Assessment' : null,
    data?.screening?.includeInterview !== false ? 'Interview' : null,
  ].filter(Boolean);
  // Mirrors the recruiter's openCandidateResumeBreakdown: show the row immediately,
  // then hydrate the full candidate behind it. Without the hydration step the modal
  // received only the lite table row, so its profile-detail sections were blank on
  // the share link while the recruiter saw them populated.
  const openInsight = async (c) => {
    const candidateId = c?._id || c?.id;
    setSelectedCandidate(c);
    if (!candidateId || candidateDetailsById[candidateId]) return;
    try {
      setCandidateDetailsLoading(true);
      const res = await axios.get(
        `/api/screenings/public/candidate-review/${shareCode}/candidates/${candidateId}`
      );
      const detailed = res.data?.candidate || null;
      if (detailed) {
        setCandidateDetailsById((prev) => ({ ...prev, [candidateId]: detailed }));
      }
    } catch {
      // Soft-fail: the modal still renders from the table row, exactly as before.
      // A toast here would fire on a link the reviewer cannot fix.
    } finally {
      setCandidateDetailsLoading(false);
    }
  };
  const parseExperienceYearsForFilter = (c) => {
    if (
      c?.experienceYears != null &&
      typeof c.experienceYears === 'number' &&
      !Number.isNaN(c.experienceYears)
    ) {
      return c.experienceYears;
    }
    const str = c?.experience;
    if (!str || typeof str !== 'string') return null;
    const s = str.trim();
    const rangeMatch = s.match(/(\d+(?:\.\d+)?)\s*[-–to]+\s*(\d+(?:\.\d+)?)/i);
    if (rangeMatch) {
      const a = parseFloat(rangeMatch[1], 10);
      const b = parseFloat(rangeMatch[2], 10);
      if (!Number.isNaN(a) && !Number.isNaN(b)) return (a + b) / 2;
    }
    const singleMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:years?|yoe)?/i);
    const n = singleMatch ? parseFloat(singleMatch[1], 10) : null;
    return n != null && !Number.isNaN(n) ? n : null;
  };
  const parseAgeYearsForFilter = (c) => {
    const raw = c?.ageYears ?? c?.age;
    if (raw == null) return null;
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).trim());
    return n != null && !Number.isNaN(n) ? n : null;
  };
  const parseCurrentCtcLacsForFilter = (c) => {
    if (
      c?.currentCtcLacs != null &&
      typeof c.currentCtcLacs === 'number' &&
      !Number.isNaN(c.currentCtcLacs)
    ) {
      return c.currentCtcLacs;
    }
    const raw = c?.currentCtc;
    if (!raw) return null;
    const s = String(raw).toLowerCase();
    const numMatch = s.match(/(\d+(?:\.\d+)?)/);
    if (!numMatch) return null;
    const n = parseFloat(numMatch[1]);
    return !Number.isNaN(n) ? n : null;
  };

  const getJobMeta = () => {
    const screening = data?.screening || {};
    const linkedJob = screening?.jobId && typeof screening.jobId === 'object' ? screening.jobId : {};
    const normalizedDescription = [
      linkedJob.description,
      screening.jobDescription,
      screening.description,
      screening.overview,
    ]
      .map((v) => (v == null ? '' : String(v).trim()))
      .find(Boolean) || '';
    const normalizedSkills =
      Array.isArray(screening.skills) && screening.skills.length > 0
        ? screening.skills.filter(Boolean)
        : Array.isArray(linkedJob.skills)
          ? linkedJob.skills.filter(Boolean)
          : [];
    return {
      title: screening.jobTitle || screening.roleName || linkedJob.title || screening.name || 'Job description',
      description: normalizedDescription,
      location: screening.location ?? linkedJob.location,
      locationType: screening.locationType ?? linkedJob.locationType,
      experienceMin: screening.experienceMin ?? linkedJob.experienceMin,
      experienceMax: screening.experienceMax ?? linkedJob.experienceMax,
      experienceText: screening.experience || linkedJob.experience || '',
      salaryMin: screening.salaryMin ?? linkedJob.salaryMin,
      salaryMax: screening.salaryMax ?? linkedJob.salaryMax,
      salaryCurrency: screening.salaryCurrency || linkedJob.salaryCurrency || 'USD',
      noticePeriod: screening.noticePeriod || linkedJob.noticePeriod || '',
      candidateQualification: screening.candidateQualification || linkedJob.candidateQualification || '',
      ageMin: screening.ageMin ?? linkedJob.ageMin,
      ageMax: screening.ageMax ?? linkedJob.ageMax,
      genderPreference: screening.genderPreference || linkedJob.genderPreference || '',
      keyResponsibilities: screening.keyResponsibilities || linkedJob.keyResponsibilities || '',
      keyAttributes: screening.keyAttributes || linkedJob.keyAttributes || '',
      skills: normalizedSkills,
    };
  };

  const handleDownloadJobPdf = () => {
    const jobMeta = getJobMeta();
    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const margin = 15;
      let y = 20;

      const safeTitle = (jobMeta.title || 'Job Description')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      doc.setFontSize(16);
      doc.text(safeTitle || 'Job Description', margin, y);
      y += 8;

      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
      y += 8;

      const addSection = (title, body) => {
        if (!body) return;
        doc.setFontSize(11);
        doc.setTextColor(60);
        doc.text(title, margin, y);
        y += 5;
        doc.setFontSize(10);
        doc.setTextColor(40);
        const lines = doc.splitTextToSize(body, 180);
        lines.forEach((line) => {
          if (y > 280) {
            doc.addPage();
            y = 20;
          }
          doc.text(line, margin, y);
          y += 5;
        });
        y += 4;
      };

      addSection('Job Overview', jobMeta.description || 'No description available for this job.');

      const details = [];
      const hasLocation =
        (Array.isArray(jobMeta.location) ? jobMeta.location.length > 0 : jobMeta.location) || jobMeta.locationType;
      if (hasLocation) details.push(`Location: ${formatLocationDisplay(jobMeta.location, jobMeta.locationType)}`);
      if (jobMeta.experienceMin != null || jobMeta.experienceMax != null) {
        details.push(`Experience: ${jobMeta.experienceMin ?? '0'}–${jobMeta.experienceMax ?? '0'} years`);
      } else if (jobMeta.experienceText) {
        details.push(`Experience: ${jobMeta.experienceText}`);
      }
      if (jobMeta.salaryMin != null || jobMeta.salaryMax != null) {
        details.push(
          `Compensation: ${jobMeta.salaryCurrency || 'USD'} ${jobMeta.salaryMin ?? 0}–${jobMeta.salaryMax ?? 0}`
        );
      }
      if (jobMeta.noticePeriod) details.push(`Notice period: ${jobMeta.noticePeriod}`);
      const candidateProfileLines = [];
      if (jobMeta.candidateQualification) candidateProfileLines.push(jobMeta.candidateQualification);
      if (jobMeta.ageMin != null || jobMeta.ageMax != null) {
        candidateProfileLines.push(`Age: ${jobMeta.ageMin ?? '—'}–${jobMeta.ageMax ?? '—'}`);
      }
      if (jobMeta.genderPreference && jobMeta.genderPreference !== 'any') {
        candidateProfileLines.push(`Gender preference: ${jobMeta.genderPreference}`);
      }
      if (candidateProfileLines.length > 0) {
        details.push(`Candidate profile: ${candidateProfileLines.join(' | ')}`);
      }
      if (details.length > 0) {
        addSection('Key details', details.join('\n'));
      }

      if (jobMeta.keyResponsibilities) {
        addSection('Key responsibilities', jobMeta.keyResponsibilities);
      }
      if (jobMeta.keyAttributes) {
        addSection('Key attributes', jobMeta.keyAttributes);
      }
      if (jobMeta.skills.length > 0) {
        addSection('Skills', jobMeta.skills.join(', '));
      }

      const fileSafeTitle = (jobMeta.title || 'Job_Description')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '_')
        .trim();
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${fileSafeTitle || 'Job_Description'}_JD_${dateStr}.pdf`;

      doc.save(filename);
      toast.success(`JD downloaded as ${filename}`);
    } catch (err) {
      console.error('Failed to generate JD PDF:', err);
      toast.error('Failed to download job description PDF');
    }
  };

  const jobMeta = getJobMeta();

  return (
    <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-hidden bg-[#F7F9FC] sm:px-6 lg:px-8 desktop:px-10 pt-6 pb-12">
      {isReviewUnlocked ? (
        <>
          <div className="pl-0 pr-0 pb-4 bg-[#F7F9FC]">
            <div className="flex flex-col gap-4 mt-6 mb-0 mx-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-[#242124]">
                    Candidate Shortlist
                    <span className="text-gray-500 font-normal ml-2 text-base">
                      ({candidates.length} shared for review)
                    </span>
                  </h2>
                  <div className="mt-2 min-w-0">
                    {data?.screening?.jobTitle ? (
                      <p className="text-lg font-semibold text-gray-900 break-words">{data.screening.jobTitle}</p>
                    ) : (
                      <p className="text-lg font-semibold text-gray-500">Role details unavailable</p>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                      {data?.screening?.companyName && (
                        <span className="inline-flex items-center">
                          <span className="font-medium text-gray-700">Company:</span>
                          <span className="ml-1 break-words">{data.screening.companyName}</span>
                        </span>
                      )}
                      {data?.screening?.name && (
                        <span className="inline-flex items-center">
                          <span className="font-medium text-gray-700">Screening:</span>
                          <span className="ml-1 break-words">{data.screening.name}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setActivityDrawerOpen(true)}
                    className="relative inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                    title="View review activity"
                  >
                    <ClockIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Activity</span>
                    {activityLog.length > 0 && (
                      <span className="inline-flex items-center justify-center rounded-full bg-[#007BFF]/10 text-[#007BFF] text-[11px] font-semibold min-w-[18px] h-[18px] px-1">
                        {activityLog.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowJobDescriptionModal(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    View Job Overview
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#007BFF]/10 text-[#007BFF] text-[10px] font-semibold">
                    {reviewerName
                      .trim()
                      .split(/\s+/)
                      .map((p) => p[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                  <span>
                    Reviewing as{' '}
                    <span className="font-semibold text-gray-900">{reviewerName}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setNameModal({
                        open: true,
                        mode: 'edit',
                        actionLabel: '',
                        queued: null,
                        required: false,
                      })
                    }
                    className="inline-flex items-center text-[11px] text-[#007BFF] hover:underline"
                  >
                    <PencilSquareIcon className="h-3 w-3 mr-0.5" />
                    change
                  </button>
                </span>
              </div>
              <div className="text-sm text-gray-600 mt-0.5">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  {recruiterName && (
                    <span className="whitespace-nowrap">
                      <span className="font-medium text-gray-800">Recruiter:</span>{' '}
                      {recruiterName}
                    </span>
                  )}
                  {evaluationBasis.length > 0 && (
                    <span className="flex flex-wrap items-center gap-1">
                      <span className="font-medium text-gray-800">Evaluation:</span>
                      {evaluationBasis.map((item) => (
                        <span
                          key={item}
                          className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 text-xs font-medium"
                        >
                          {item}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#F7F9FC] rounded-xl shadow-sm overflow-hidden">
            {candidates.length === 0 ? (
              <div className="text-center py-16 px-6 bg-white rounded-xl border border-gray-200">
                <p className="text-gray-600">No candidates in this review.</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-w-full bg-white rounded-xl border border-gray-200">
                <CandidatesTable
                  rows={candidates}
                  screeningMeta={screeningMeta}
                  options={tableOptions}
                  // Without these two the table falls back to useAuth, which is
                  // empty on a public link: the overall score silently became an
                  // unweighted average, and every custom column vanished.
                  weightsConfig={weightsConfig}
                  registrationFields={viewConfig?.registrationFields || []}
                  notesShareCode={shareCode}
                  notesReviewerName={reviewerName}
                  onCandidateNoteAdded={() => refreshActivityLog()}
                  scoreFilters={scoreFilters}
                  setScoreFilters={setScoreFilters}
                  metaFilters={metaFilters}
                  setMetaFilters={setMetaFilters}
                  shortlistDecisions={shortlistDecisions}
                  setShortlistDecisions={setShortlistDecisions}
                  shortlistNotes={shortlistNotes}
                  setShortlistNotes={setShortlistNotes}
                  patchCandidateShortlistField={patchCandidateShortlistField}
                  openCandidateResumeBreakdown={openInsight}
                  handleInlineViewAssessmentScore={(c) => setAssessmentModal({ open: true, candidate: c })}
                  handleInlineViewAssessmentProctoring={(c) => setAssessmentModal({ open: true, candidate: c })}
                  openInterviewSessionForCandidate={(c, initialTab = 'analysis') =>
                    setInterviewModal({ open: true, candidate: c, initialTab })
                  }
                  parseExperienceYearsForFilter={parseExperienceYearsForFilter}
                  parseAgeYearsForFilter={parseAgeYearsForFilter}
                  parseCurrentCtcLacsForFilter={parseCurrentCtcLacsForFilter}
                />
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center justify-center gap-1 text-xs text-gray-400">
            <span>Powered by</span>
            <img
              src="/logo.svg"
              alt="TalentCrux"
              className="h-5 w-5 object-contain"
            />
            <span className="font-medium text-gray-500">TalentCrux</span>
          </div>
        </>
      ) : (
        <div className="mx-4 mt-6 rounded-2xl border border-gray-200 bg-white px-6 py-16 text-center shadow-sm">
          <div className="mx-auto max-w-md">
            <h2 className="text-xl font-semibold text-gray-900">Shared review access</h2>
            <p className="mt-2 text-sm text-gray-600">
              Enter your name to view candidates and continue with this shared review link.
            </p>
          </div>
        </div>
      )}

      <CandidateInsightModal
        isOpen={!!selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        // Detail spread OVER the row, same precedence as the recruiter page: the
        // hydrated document wins, and the row remains the fallback until it lands
        // (and if the fetch failed).
        candidate={(() => {
          if (!selectedCandidate) return null;
          const candidateId = selectedCandidate._id || selectedCandidate.id;
          const detailed = candidateId ? candidateDetailsById[candidateId] : null;
          return detailed ? { ...selectedCandidate, ...detailed } : selectedCandidate;
        })()}
        detailsLoading={candidateDetailsLoading}
        initialTab="score"
        publicMode
        publicShareCode={shareCode}
      />

      <PublicAssessmentScoreModal
        isOpen={assessmentModal.open}
        onClose={() => setAssessmentModal({ open: false, candidate: null })}
        shareCode={shareCode}
        candidate={assessmentModal.candidate}
      />

      <ReviewerNameModal
        isOpen={nameModal.open}
        initialName={nameModal.mode === 'edit' ? reviewerName : ''}
        actionLabel={nameModal.actionLabel}
        isRequired={nameModal.required}
        onSubmit={handleNameSubmit}
        onCancel={handleNameCancel}
      />

      <ReviewActivityDrawer
        isOpen={activityDrawerOpen}
        onClose={() => setActivityDrawerOpen(false)}
        activities={activityLog}
      />

      <PublicInterviewReplayModal
        isOpen={interviewModal.open}
        onClose={() => setInterviewModal({ open: false, candidate: null, initialTab: 'analysis' })}
        shareCode={shareCode}
        candidate={interviewModal.candidate}
        interviewTitle={data?.screening?.interviewTitle || ''}
      />

      {showJobDescriptionModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-[2px] px-4">
          <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {jobMeta.title || 'Job description'}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDownloadJobPdf}
                  className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  aria-label="Download job description as PDF"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowJobDescriptionModal(false)}
                  className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  aria-label="Close job description"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 flex-1 overflow-y-auto space-y-6">
              {/* Overview */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Job Overview
                </h3>
                <p className="text-sm text-gray-800 whitespace-pre-line">
                  {jobMeta.description || 'No description available for this job.'}
                </p>
              </div>

              {/* Key details grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(Array.isArray(jobMeta.location) ? jobMeta.location.length > 0 : jobMeta.location) ||
                  jobMeta.locationType ? (
                  <div className="text-xs text-gray-500">
                    <div className="font-semibold uppercase tracking-wide mb-0.5">Location</div>
                    <div className="text-sm text-gray-800">
                      {formatLocationDisplay(jobMeta.location, jobMeta.locationType)}
                    </div>
                  </div>
                ) : null}

                {(jobMeta.experienceMin != null || jobMeta.experienceMax != null) ? (
                  <div className="text-xs text-gray-500">
                    <div className="font-semibold uppercase tracking-wide mb-0.5">
                      Experience range
                    </div>
                    <div className="text-sm text-gray-800">
                      {jobMeta.experienceMin ?? '0'}–{jobMeta.experienceMax ?? '0'} years
                    </div>
                  </div>
                ) : jobMeta.experienceText ? (
                  <div className="text-xs text-gray-500">
                    <div className="font-semibold uppercase tracking-wide mb-0.5">
                      Experience range
                    </div>
                    <div className="text-sm text-gray-800">{jobMeta.experienceText}</div>
                  </div>
                ) : null}

                {(jobMeta.salaryMin != null || jobMeta.salaryMax != null) ? (
                  <div className="text-xs text-gray-500">
                    <div className="font-semibold uppercase tracking-wide mb-0.5">Compensation</div>
                    <div className="text-sm text-gray-800">
                      {jobMeta.salaryCurrency || 'USD'} {jobMeta.salaryMin ?? 0}–{jobMeta.salaryMax ?? 0}
                    </div>
                  </div>
                ) : null}

                {jobMeta.noticePeriod ? (
                  <div className="text-xs text-gray-500">
                    <div className="font-semibold uppercase tracking-wide mb-0.5">Notice Period</div>
                    <div className="text-sm text-gray-800">{jobMeta.noticePeriod}</div>
                  </div>
                ) : null}

                {(jobMeta.candidateQualification ||
                  jobMeta.genderPreference ||
                  jobMeta.ageMin != null ||
                  jobMeta.ageMax != null) ? (
                  <div className="text-xs text-gray-500">
                    <div className="font-semibold uppercase tracking-wide mb-0.5">
                      Candidate profile
                    </div>
                    <div className="space-y-1 text-sm text-gray-800">
                      {jobMeta.candidateQualification && <div>{jobMeta.candidateQualification}</div>}
                      {(jobMeta.ageMin != null || jobMeta.ageMax != null) && (
                        <div>
                          Age: {jobMeta.ageMin ?? '—'}–{jobMeta.ageMax ?? '—'}
                        </div>
                      )}
                      {jobMeta.genderPreference && jobMeta.genderPreference !== 'any' && (
                        <div>Gender preference: {jobMeta.genderPreference}</div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              {jobMeta.keyResponsibilities ? (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Key responsibilities
                  </h3>
                  <p className="text-sm text-gray-800 whitespace-pre-line">
                    {jobMeta.keyResponsibilities}
                  </p>
                </div>
              ) : null}

              {jobMeta.keyAttributes ? (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Key attributes
                  </h3>
                  <p className="text-sm text-gray-800 whitespace-pre-line">{jobMeta.keyAttributes}</p>
                </div>
              ) : null}

              {jobMeta.skills.length > 0 ? (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {jobMeta.skills.map((skill, idx) => (
                      <span
                        key={`${skill}-${idx}`}
                        className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default CandidateReviewPublic;
